/**
 * Category Scoping Utility for Multi-Type Education Institutions
 * ──────────────────────────────────────────────────────────────────
 * Provides intelligent filtering and terminology adaptation for:
 *   - School (K-12, CBSE, ICSE, State Boards)
 *   - College (Universities, Higher Education, Degree, Semesters)
 *   - Coaching (Competitive Exams, Test Prep, IIT-JEE, NEET, Foundation)
 *   - Common / General (Shared core ERP modules)
 */

export const ARCHETYPES = {
  SCHOOL: 'school',
  COLLEGE: 'college',
  COACHING: 'coaching',
  OTHER: 'other',
};

/**
 * Resolve standard archetype string from category and subcategory values
 * @param {string} category 
 * @param {string} subcategory 
 * @returns {'school' | 'college' | 'coaching' | 'other'}
 */
export function resolveInstituteArchetype(category, subcategory) {
  const cat = String(category || '').trim().toLowerCase();
  const sub = String(subcategory || '').trim().toLowerCase();
  const combined = `${cat} ${sub}`;

  // School coaching / tuition teaches school students in courses and batches, so it is a
  // coaching institute even though its name (and often its subcategory) says "school".
  if (/school[\s_-]*coaching|tuition/.test(cat)) {
    return ARCHETYPES.COACHING;
  }

  if (
    combined.includes('school') ||
    combined.includes('k12') ||
    combined.includes('k-12') ||
    combined.includes('play school') ||
    combined.includes('secondary')
  ) {
    return ARCHETYPES.SCHOOL;
  }

  if (
    combined.includes('college') ||
    combined.includes('university') ||
    combined.includes('higher ed') ||
    combined.includes('polytechnic') ||
    combined.includes('degree')
  ) {
    return ARCHETYPES.COLLEGE;
  }

  // Any non-school, non-college institution in CoachHub operates under Coaching / Batch-based archetype
  return ARCHETYPES.COACHING;
}

/**
 * Check if a navigation item should be visible for the current archetype
 * Rules:
 *   - School: Hide Coaching items (Courses, Batches, Advanced Batch Reports).
 *             Show School items (Students, Attendance, Classes, Sections, Marksheets, Report Cards, Timetable, ID Cards, Certificates).
 *   - College: Hide Coaching Batches. Show Degrees/Courses, Marksheets, Timetable.
 *   - Coaching: Hide School Marksheets & Onboarding. Show Courses, Batches, DPP, Merit List, etc.
 *
 * @param {object} item - Navigation item definition
 * @param {string} currentArchetype - 'school' | 'college' | 'coaching' | 'other'
 * @returns {boolean}
 */
export function isItemVisibleForCategory(item, currentArchetype, settings = {}) {
  if (!item) return false;

  // 0. Explicit category whitelist on item definition (e.g., categories: ['school', 'college'])
  if (Array.isArray(item.categories) && item.categories.length > 0) {
    if (currentArchetype && currentArchetype !== ARCHETYPES.OTHER && !item.categories.includes(currentArchetype)) {
      return false;
    }
  }

  // 1. Marksheet / Report Cards (Applicable to School, College, and Coaching with scoped terminology)
  const isMarksheetFoundation = item.path === '/institute-panel/marksheet-foundation' || item.label === 'Marksheet Setup';
  if (isMarksheetFoundation && currentArchetype === ARCHETYPES.COACHING) {
    return false; // CCE / CBSE Foundation setup is School/College specific
  }

  // 2. School Onboarding Guide (Only for Schools)
  const isSchoolGuide =
    item.path === '/institute-panel/school-onboarding' ||
    item.label === 'School Onboarding Guide' ||
    item.label === 'School Onboarding';
  if (isSchoolGuide && currentArchetype !== ARCHETYPES.SCHOOL) {
    return false;
  }

  // 3. Courses (/institute-panel/courses)
  // In School K-12, students are admitted to Classes (Class 1 to 12), not "Courses".
  const isCourses = item.path === '/institute-panel/courses' || item.label === 'Courses';
  if (isCourses && currentArchetype === ARCHETYPES.SCHOOL) {
    return false;
  }

  // 4. Batches & Batch-Specific Reports (/institute-panel/batches, /advanced-batch-report)
  // In School & College, students are grouped by Section or Division/Semester, not coaching Batches.
  const isBatches =
    item.path === '/institute-panel/batches' ||
    item.path === '/institute-panel/advanced-batch-report' ||
    item.path === '/institute-panel/batch-performance' ||
    item.label === 'Batches' ||
    item.label === 'Advanced Batch Report' ||
    item.label === 'Batch Performance';
  if (isBatches && (currentArchetype === ARCHETYPES.SCHOOL || currentArchetype === ARCHETYPES.COLLEGE)) {
    return false;
  }

  // 5. Practice papers: category defaults with an institute-level override.
  const isDpp = item.path === '/institute-panel/dpp' || item.path === '/student/dpp' || item.label === 'Daily Practice Papers';
  if (isDpp && !getDppCategoryConfig(currentArchetype, settings.dpp_enabled).enabled) {
    return false;
  }

  // 6. Rank & Merit List (Coaching / Test Prep specific — schools use Report Cards & Marksheets)
  const isMeritList = item.path === '/institute-panel/merit-list' || item.label === 'Rank & Merit List';
  if (isMeritList && currentArchetype === ARCHETYPES.SCHOOL) {
    return false;
  }

  return true;
}

// Coaching and tuition institutes do not issue marksheets or report cards: their results are
// tests, ranks and the merit list.
export function isMarksheetEnabledForCategory(archetype) {
  return archetype !== ARCHETYPES.COACHING;
}

/**
 * Get category-specific terminology and configuration for Marksheet & Result Management
 * @param {string} archetype - 'school' | 'college' | 'coaching' | 'other'
 * @returns {object}
 */
export function getMarksheetCategoryConfig(archetype) {
  switch (archetype) {
    case ARCHETYPES.SCHOOL:
      return {
        moduleTitle: 'Marksheet & Result Management',
        badgeTitle: 'CBSE / Board Marksheet Engine',
        groupingLabel: 'Class & Section',
        filterLabel: 'All Classes',
        statGroupLabel: 'Classes',
        pdfTitle: 'OFFICIAL ANNUAL ACADEMIC PROGRESS REPORT',
        hasCceCbse: true,
        showClasses: true,
        showBatches: false,
      };
    case ARCHETYPES.COLLEGE:
      return {
        moduleTitle: 'Grade Sheet & Result Management',
        badgeTitle: 'Semester & SGPA/CGPA Grading Engine',
        groupingLabel: 'Programme / Semester',
        filterLabel: 'All Programmes / Sems',
        statGroupLabel: 'Programmes / Sems',
        pdfTitle: 'OFFICIAL SEMESTER GRADE REPORT & TRANSCRIPT',
        hasCceCbse: true,
        showClasses: true,
        showBatches: true,
      };
    case ARCHETYPES.COACHING:
    default:
      return {
        moduleTitle: 'Test Marksheet & Result Management',
        badgeTitle: 'Test Series & Assessment Engine',
        groupingLabel: 'Course / Batch',
        filterLabel: 'All Batches',
        statGroupLabel: 'Batches',
        pdfTitle: 'OFFICIAL TEST PERFORMANCE & MARKSHEET',
        hasCceCbse: false,
        showClasses: false,
        showBatches: true,
      };
  }
}

export function isCourseCategoriesEnabledForCategory(archetype) {
  return archetype !== ARCHETYPES.SCHOOL;
}

export function isBatchesEnabledForCategory(archetype) {
  return archetype === ARCHETYPES.COACHING || archetype === ARCHETYPES.OTHER;
}

export function isCoursesEnabledForCategory(archetype) {
  return archetype !== ARCHETYPES.SCHOOL;
}

/**
 * Get human-readable label and icon details for archetype
 * @param {string} archetype 
 * @returns {{ label: string, shortBadge: string, description: string, color: string }}
 */
export function getArchetypeMeta(archetype) {
  switch (archetype) {
    case ARCHETYPES.SCHOOL:
      return {
        label: 'School Edition (K-12)',
        shortBadge: 'School',
        description: 'CBSE, ICSE & State Board School Operations',
        color: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      };
    case ARCHETYPES.COLLEGE:
      return {
        label: 'College & University Edition',
        shortBadge: 'College',
        description: 'Higher Education, Degree & Campus Operations',
        color: 'bg-blue-100 text-blue-800 border-blue-300',
      };
    case ARCHETYPES.COACHING:
      return {
        label: 'Coaching & Test Prep Edition',
        shortBadge: 'Coaching',
        description: 'Competitive Exams, Batches & Lead Funnel',
        color: 'bg-purple-100 text-purple-800 border-purple-300',
      };
    default:
      return {
        label: 'Standard ERP',
        shortBadge: 'Standard',
        description: 'Complete Education Management',
        color: 'bg-slate-100 text-slate-800 border-slate-300',
      };
  }
}

export function getDppCategoryConfig(archetype, enabled) {
  const titles = { school: ['Daily Practice / Worksheets', 'Worksheet'], college: ['Practice Assignments / Problem Sets', 'Practice Assignment'] };
  const [title, singular] = titles[archetype] || ['Daily Practice Papers (DPP)', 'DPP'];
  return { title, singular, enabled: typeof enabled === 'boolean' ? enabled : !['school', 'college'].includes(archetype) };
}
